import { useEffect, useState, useCallback } from "react";
import { Cloud, FolderOpen, Key, Wrench, AlertTriangle } from "lucide-react";
import { theme, card, inputStyle } from "../theme";
import { api } from "../api/client";
import { safeHref, openSafe } from "../utils/safeHref";
import { EvidenceImportPanel } from "../components/EvidenceImportPanel";

import { cbSafe } from "../lib/colourBlind";
type Provider = "onedrive" | "sharepoint" | "azure_blob_sas" | "azure_blob_sp";

interface StorageStatus {
  provider: Provider | null;
  displayName: string | null;
  folderWebUrl: string | null;
  folderName: string | null;
  sasExpiry: string | null;
  /** Set while Microsoft rejects the stored sign-in: the connection exists but cannot be used until it is re-authorised. */
  authError: { message: string; at: string | null } | null;
}

interface ProviderOption {
  id: Provider;
  label: string;
  icon: React.ReactNode;
  tagline: string;
  description: string;
}

const PROVIDERS: ProviderOption[] = [
  {
    id: "onedrive",
    label: "Microsoft OneDrive",
    icon: <Cloud size={20} />,
    tagline: "Personal / business OneDrive",
    description: "Mirror evidence to a named folder in the authorising user's Microsoft 365 OneDrive for Business account.",
  },
  {
    id: "sharepoint",
    label: "Microsoft SharePoint",
    icon: <FolderOpen size={20} />,
    tagline: "SharePoint document library",
    description: "Mirror evidence into a specific SharePoint site's document library — ideal for organisations with shared intranet storage.",
  },
  {
    id: "azure_blob_sas",
    label: "Azure Blob (SAS URL)",
    icon: <Key size={20} />,
    tagline: "Your own Azure storage, SAS token",
    description: "Provide a container-level SAS URL from your own Azure storage account. Simple to configure; remember to renew the token before it expires.",
  },
  {
    id: "azure_blob_sp",
    label: "Azure Blob (Service Principal)",
    icon: <Wrench size={20} />,
    tagline: "Your own Azure storage, long-lived credentials",
    description: "Register a service principal in your Azure tenant, grant it Storage Blob Data Contributor, then provide its credentials here. No expiry to manage.",
  },
];

const PROVIDER_LABELS: Record<Provider, string> = {
  onedrive: "Microsoft OneDrive",
  sharepoint: "Microsoft SharePoint",
  azure_blob_sas: "Azure Blob (SAS)",
  azure_blob_sp: "Azure Blob (Service Principal)",
};

export function ConnectStoragePage() {
  const [status, setStatus]           = useState<StorageStatus | null>(null);
  const [loadingStatus, setLoading]   = useState(true);
  const [disconnecting, setDisconn]   = useState(false);
  const [banner, setBanner]           = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [selectedProvider, setSelected] = useState<Provider | null>(null);

  // Shared folder name state (used across all connect forms)
  const [folderName, setFolderName]   = useState("Evidence Repository");
  const [connecting, setConnecting]   = useState(false);

  // SharePoint-specific
  const [spSiteUrl, setSpSiteUrl]     = useState("");

  // Blob SAS-specific
  const [sasSasUrl, setSasSasUrl]     = useState("");

  // Blob SP-specific
  const [spTenantId, setSpTenantId]   = useState("");
  const [spClientId, setSpClientId]   = useState("");
  const [spSecret, setSpSecret]       = useState("");
  const [spAccount, setSpAccount]     = useState("");
  const [spContainer, setSpContainer] = useState("");

  // Re-authorising a Graph connection Microsoft has started rejecting
  const [reconnectingSp, setReconnectingSp] = useState(false);

  // Rename state
  const [renaming, setRenaming]       = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  // Parse OAuth callback query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "true") {
      setBanner({ kind: "success", text: "Cloud storage connected successfully!" });
      window.history.replaceState({}, "", window.location.pathname);
      const t = setTimeout(() => setBanner(null), 4000);
      return () => clearTimeout(t);
    }
    const error = params.get("error");
    if (error === "invalid_state") {
      setBanner({ kind: "error", text: "Connection failed — the link expired. Please try again." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (error === "auth_failed") {
      setBanner({ kind: "error", text: "Microsoft did not authorise the connection. Please try again — if it keeps failing, ask your administrator to check the app's consent for your account." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (error === "connect_failed") {
      setBanner({ kind: "error", text: "Microsoft authorised the connection but it could not be saved. Please try again." });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<StorageStatus>("/storage/status");
      setStatus(data);
      if (data.folderName) setFolderName(data.folderName);
    } catch {
      // Non-fatal — leave status null.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // ── SAS expiry warning ────────────────────────────────────────────────────

  const sasWarning = (() => {
    if (!status?.sasExpiry) return null;
    const daysLeft = Math.ceil((new Date(status.sasExpiry).getTime() - Date.now()) / 86_400_000);
    if (daysLeft > 30) return null;
    return daysLeft <= 0
      ? { text: "SAS token has expired — uploads will fail. Please reconnect with a new SAS URL.", kind: "error" as const }
      : { text: `SAS token expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}. Reconnect before it expires to avoid interruption.`, kind: "warning" as const };
  })();

  // ── Connect handlers ──────────────────────────────────────────────────────

  async function handleConnectOneDrive() {
    setConnecting(true);
    try {
      const params = new URLSearchParams({ folderName });
      const data = await api.get<{ url: string }>(`/storage/onedrive/auth-url?${params}`);
      window.location.href = data.url;
    } catch {
      setBanner({ kind: "error", text: "Failed to start OneDrive connection. Please try again." });
      setConnecting(false);
    }
  }

  async function handleConnectSharePoint() {
    if (!spSiteUrl.trim()) { setBanner({ kind: "error", text: "Please enter your SharePoint site URL." }); return; }
    setConnecting(true);
    try {
      const params = new URLSearchParams({ siteUrl: spSiteUrl.trim(), folderName });
      const data = await api.get<{ url: string }>(`/storage/sharepoint/auth-url?${params}`);
      window.location.href = data.url;
    } catch (err: unknown) {
      const msg = (err as { payload?: { error?: string } })?.payload?.error || "Failed to start SharePoint connection.";
      setBanner({ kind: "error", text: msg });
      setConnecting(false);
    }
  }

  async function handleConnectBlobSas() {
    if (!sasSasUrl.trim()) { setBanner({ kind: "error", text: "Please enter a SAS URL." }); return; }
    setConnecting(true);
    try {
      await api.post("/storage/blob-sas/connect", { sasUrl: sasSasUrl.trim(), folderName });
      await fetchStatus();
      setBanner({ kind: "success", text: "Azure Blob Storage (SAS) connected." });
      setSelected(null);
    } catch (err: unknown) {
      const msg = (err as { payload?: { message?: string } })?.payload?.message || "SAS validation failed. Check the URL and try again.";
      setBanner({ kind: "error", text: msg });
    } finally {
      setConnecting(false);
    }
  }

  async function handleConnectBlobSp() {
    if (!spTenantId.trim() || !spClientId.trim() || !spSecret.trim() || !spAccount.trim() || !spContainer.trim()) {
      setBanner({ kind: "error", text: "All fields are required." });
      return;
    }
    setConnecting(true);
    try {
      await api.post("/storage/blob-sp/connect", {
        tenantId: spTenantId.trim(),
        clientId: spClientId.trim(),
        clientSecret: spSecret.trim(),
        storageAccount: spAccount.trim(),
        container: spContainer.trim(),
        folderName,
      });
      await fetchStatus();
      setBanner({ kind: "success", text: "Azure Blob Storage (Service Principal) connected." });
      setSelected(null);
    } catch (err: unknown) {
      const msg = (err as { payload?: { message?: string } })?.payload?.message || "Credential validation failed. Check your details and try again.";
      setBanner({ kind: "error", text: msg });
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconn(true);
    try {
      await api.del("/storage/disconnect");
      await fetchStatus();
      setBanner({ kind: "success", text: "Cloud storage disconnected." });
    } catch {
      setBanner({ kind: "error", text: "Failed to disconnect. Please try again." });
    } finally {
      setDisconn(false);
    }
  }

  async function handleRename() {
    if (!renameDraft.trim() || renameDraft.trim() === status?.folderName) { setRenaming(false); return; }
    setRenameLoading(true);
    try {
      const data = await api.patch<{ folderName: string; folderWebUrl: string | null }>("/storage/folder", { folderName: renameDraft.trim() });
      setStatus((s) => s ? { ...s, folderName: data.folderName, folderWebUrl: data.folderWebUrl } : s);
      setFolderName(data.folderName);
      setBanner({ kind: "success", text: `Folder renamed to "${data.folderName}".` });
      setRenaming(false);
    } catch {
      setBanner({ kind: "error", text: "Failed to rename folder. Please try again." });
    } finally {
      setRenameLoading(false);
    }
  }

  // ── Styles ────────────────────────────────────────────────────────────────

  const btnPrimary: React.CSSProperties = {
    border: `1px solid ${theme.color.accent}`,
    background: theme.color.accentBg,
    borderRadius: 3,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 700,
    color: theme.color.accentText,
    cursor: "pointer",
  };

  const btnDanger: React.CSSProperties = {
    background: "transparent",
    border: cbSafe("1px solid #7f1d1d"),
    borderRadius: 3,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: theme.color.dangerText,
    cursor: "pointer",
  };

  const btnGhost: React.CSSProperties = {
    background: "transparent",
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 3,
    padding: "8px 12px",
    fontSize: 12,
    color: theme.color.muted,
    cursor: "pointer",
  };

  const fieldLabel: React.CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: theme.color.heading,
    marginBottom: 5,
  };

  const fieldWrap: React.CSSProperties = { marginBottom: 14 };

  const helpText: React.CSSProperties = { fontSize: 11, color: theme.color.faint, marginTop: 3, lineHeight: 1.5 };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Cloud Storage
      </h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        Mirror your compliance documents and evidence files to your own cloud storage destination.
        Choose from Microsoft OneDrive, SharePoint, or your own Azure Blob Storage container.
      </p>

      {/* Banner */}
      {banner && (
        <div style={{
          ...card, marginBottom: 20,
          border: `1px solid ${banner.kind === "success" ? theme.color.success : theme.color.danger}`,
          background: banner.kind === "success" ? cbSafe("#16a34a10") : cbSafe("#dc262610"),
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 13, color: banner.kind === "success" ? theme.color.successText : theme.color.dangerText }}>
            {banner.text}
          </span>
          <button onClick={() => setBanner(null)} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 16 }}>×</button>
        </div>
      )}

      {loadingStatus && (
        <div style={{ ...card, color: theme.color.subtle, fontSize: 13 }}>Loading connection status…</div>
      )}

      {/* ── Connected state ── */}
      {!loadingStatus && status?.provider && (
        <>
          <EvidenceImportPanel compact sourceFilter={["onedrive", "sharepoint"]} />

          {/* SAS expiry warning */}
          {sasWarning && (
            <div style={{
              ...card, marginBottom: 16,
              border: `1px solid ${sasWarning.kind === "error" ? theme.color.danger : theme.color.warning}`,
              background: sasWarning.kind === "error" ? theme.color.dangerBg : theme.color.warningBg,
            }}>
              <span style={{ fontSize: 13, color: sasWarning.kind === "error" ? theme.color.dangerText : theme.color.warningText, display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} /> {sasWarning.text}
              </span>
            </div>
          )}

          {/* Status card — amber while Microsoft rejects the stored sign-in */}
          <div style={{
            ...card, marginBottom: 16,
            border: `1px solid ${status.authError ? theme.color.warning : theme.color.success}`,
            background: status.authError ? theme.color.warningBg : cbSafe("#16a34a0a"),
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: status.authError ? theme.color.warningText : theme.color.successText,
                  background: status.authError ? "transparent" : cbSafe("#16a34a20"),
                  border: `1px solid ${status.authError ? theme.color.warning : theme.color.success}`,
                  borderRadius: 3, padding: "3px 10px",
                }}>
                  {status.authError ? "Needs re-authorising" : "Connected"}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
                  {PROVIDER_LABELS[status.provider]}
                </span>
                {status.displayName && (
                  <span style={{ fontSize: 13, color: theme.color.muted }}>— {status.displayName}</span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {status.authError && status.provider === "onedrive" && (
                  <button onClick={handleConnectOneDrive} disabled={connecting} style={{ ...btnPrimary, opacity: connecting ? 0.6 : 1 }}>
                    {connecting ? "Redirecting…" : "Reconnect"}
                  </button>
                )}
                {status.authError && status.provider === "sharepoint" && !reconnectingSp && (
                  <button onClick={() => setReconnectingSp(true)} style={btnPrimary}>Reconnect</button>
                )}
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  style={{ ...btnDanger, opacity: disconnecting ? 0.6 : 1, cursor: disconnecting ? "not-allowed" : "pointer" }}
                >
                  {disconnecting ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            </div>
            {status.authError && (
              <div style={{ marginTop: 10, fontSize: 12, color: theme.color.warningText, lineHeight: 1.6 }}>
                <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
                Microsoft is rejecting the stored sign-in, so nothing can be mirrored or imported until you reconnect
                {status.authError.at ? ` (since ${new Date(status.authError.at).toLocaleString()})` : ""}.
                <div style={{ marginTop: 4, color: theme.color.muted, fontVariantNumeric: "tabular-nums", fontSize: 11, wordBreak: "break-word" }}>{status.authError.message}</div>
                {reconnectingSp && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
                    <input
                      style={{ ...inputStyle, flex: 1, minWidth: 260 }}
                      value={spSiteUrl}
                      onChange={(e) => setSpSiteUrl(e.target.value)}
                      placeholder="https://yourcompany.sharepoint.com/sites/compliance"
                      autoFocus
                    />
                    <button onClick={handleConnectSharePoint} disabled={connecting} style={{ ...btnPrimary, opacity: connecting ? 0.6 : 1 }}>
                      {connecting ? "Redirecting…" : "Reconnect SharePoint"}
                    </button>
                    <button onClick={() => setReconnectingSp(false)} style={btnGhost}>Cancel</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Folder name card */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>
              {status.provider.startsWith("azure_blob") ? "Root path prefix" : "Compliance folder"}
            </div>
            {!renaming ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, color: theme.color.heading, fontWeight: 600 }}>
                    {status.folderName || "Evidence Repository"}
                  </div>
                  {status.folderWebUrl ? (
                    <div
                      style={{ fontSize: 12, color: theme.color.accentText, cursor: "pointer", marginTop: 2, textDecoration: "underline" }}
                      onClick={() => openSafe(status.folderWebUrl)}
                    >
                      Open in {status.provider === "sharepoint" ? "SharePoint" : "OneDrive"} →
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: theme.color.faint, marginTop: 2 }}>
                      Virtual blob prefix — files land at {status.folderName ?? "Evidence Repository"}/…
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setRenameDraft(status.folderName ?? "Evidence Repository"); setRenaming(true); }}
                  style={{ ...btnPrimary, padding: "6px 12px", fontWeight: 600 }}
                >
                  Rename
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(false); }}
                  autoFocus
                />
                <button onClick={handleRename} disabled={renameLoading || !renameDraft.trim()} style={{ ...btnPrimary, opacity: renameLoading ? 0.6 : 1 }}>
                  {renameLoading ? "Saving…" : "Save"}
                </button>
                <button onClick={() => setRenaming(false)} style={btnGhost}>Cancel</button>
              </div>
            )}
          </div>

          {/* What gets synced */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Synced automatically</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
              <li style={{ fontSize: 13, color: theme.color.subtle }}>Generated policy documents (.docx / .md)</li>
              <li style={{ fontSize: 13, color: theme.color.subtle }}>Uploaded evidence documents</li>
            </ul>
          </div>
        </>
      )}

      {/* ── Disconnected state ── */}
      {!loadingStatus && !status?.provider && (
        <>
          {/* Provider picker */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>
              Choose a storage provider
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
              {PROVIDERS.map((p) => {
                const selected = selectedProvider === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelected(selected ? null : p.id)}
                    style={{
                      ...card,
                      textAlign: "left",
                      cursor: "pointer",
                      border: selected
                        ? `2px solid ${theme.color.accent}`
                        : `1px solid ${theme.color.border}`,
                      background: selected ? theme.color.accentBg : theme.color.panel,
                      outline: "none",
                      padding: "14px 16px",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <div style={{ fontSize: 22, marginBottom: 8 }}>{p.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>{p.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.accentText, marginBottom: 6 }}>{p.tagline}</div>
                    <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{p.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Setup panel for selected provider */}
          {selectedProvider && (
            <div style={{ ...card, marginBottom: 24 }}>
              {/* Shared: folder name */}
              <div style={fieldWrap}>
                <label style={fieldLabel}>
                  {selectedProvider.startsWith("azure_blob") ? "Root path prefix" : "Folder name"}
                </label>
                <input
                  style={{ ...inputStyle, maxWidth: 340 }}
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder="Evidence Repository"
                />
                <div style={helpText}>
                  {selectedProvider.startsWith("azure_blob")
                    ? "Evidence files will be stored under this prefix inside your container."
                    : "This folder will be created in your " + (selectedProvider === "sharepoint" ? "SharePoint site" : "OneDrive for Business") + "."}
                </div>
              </div>

              {/* OneDrive */}
              {selectedProvider === "onedrive" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 18 }}>
                    {[
                      { title: "Generated policy documents", desc: "Every policy document you generate will automatically appear in your dedicated OneDrive folder, ready to share with auditors." },
                      { title: "Uploaded evidence documents", desc: "Evidence files you upload to the platform are mirrored to OneDrive so your compliance folder stays complete and up to date." },
                      { title: "Dedicated compliance folder", desc: "All files land in a single named folder in your OneDrive, keeping your compliance artefacts organised and separate." },
                      { title: "Automatic sync on every change", desc: "There is nothing to remember — every time a document is generated or uploaded the sync happens silently in the background." },
                    ].map((f) => (
                      <div key={f.title} style={{ ...card, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 5 }}>{f.title}</div>
                        <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleConnectOneDrive}
                    disabled={connecting || !folderName.trim()}
                    style={{ ...btnPrimary, display: "block", width: "100%", padding: "11px 18px", fontSize: 14, opacity: (connecting || !folderName.trim()) ? 0.7 : 1, marginBottom: 12 }}
                  >
                    {connecting ? "Redirecting to Microsoft…" : "Connect Microsoft OneDrive"}
                  </button>
                  <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
                    You'll sign in to your Microsoft 365 account and approve access. We request permission to read and write files in your chosen folder only — no email or calendar access.
                  </p>
                </>
              )}

              {/* SharePoint */}
              {selectedProvider === "sharepoint" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 18 }}>
                    {[
                      { title: "Team-wide access", desc: "Compliance documents land in your SharePoint document library, visible to everyone with site access — no platform login needed." },
                      { title: "Auditor-ready", desc: "Grant your auditors access to the SharePoint site and they can download evidence directly, without needing an account here." },
                      { title: "Dedicated compliance folder", desc: "A named folder keeps all generated policies and uploaded evidence separate from other site content." },
                      { title: "Automatic sync", desc: "Every document you generate or upload is pushed to SharePoint silently in the background — nothing to remember." },
                    ].map((f) => (
                      <div key={f.title} style={{ ...card, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 5 }}>{f.title}</div>
                        <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={fieldWrap}>
                    <label style={fieldLabel}>SharePoint site URL</label>
                    <input
                      style={{ ...inputStyle, maxWidth: 440 }}
                      value={spSiteUrl}
                      onChange={(e) => setSpSiteUrl(e.target.value)}
                      placeholder="https://yourorg.sharepoint.com/sites/compliance"
                    />
                    <div style={helpText}>The URL of the SharePoint site whose document library will receive evidence files.</div>
                  </div>
                  <button
                    onClick={handleConnectSharePoint}
                    disabled={connecting || !folderName.trim() || !spSiteUrl.trim()}
                    style={{ ...btnPrimary, display: "block", width: "100%", padding: "11px 18px", fontSize: 14, opacity: (connecting || !folderName.trim() || !spSiteUrl.trim()) ? 0.7 : 1, marginBottom: 12 }}
                  >
                    {connecting ? "Redirecting to Microsoft…" : "Connect SharePoint"}
                  </button>
                  <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
                    You'll sign in to Microsoft 365 and approve Sites.ReadWrite.All access for the specified site.
                  </p>
                </>
              )}

              {/* Azure Blob SAS */}
              {selectedProvider === "azure_blob_sas" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 18 }}>
                    {[
                      { title: "Your own storage", desc: "Files go directly into a container in your own Azure subscription — your data never passes through our storage." },
                      { title: "Quick to configure", desc: "Generate a SAS URL in the Azure Portal and paste it here. No app registrations or service principals needed." },
                      { title: "Plugs into your pipeline", desc: "Evidence files in your Blob container can feed any Azure-connected workflow, Logic App, or downstream system." },
                      { title: "Billed to your account", desc: "Storage costs go against your own Azure subscription at your negotiated rates." },
                    ].map((f) => (
                      <div key={f.title} style={{ ...card, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 5 }}>{f.title}</div>
                        <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={fieldWrap}>
                    <label style={fieldLabel}>Container SAS URL</label>
                    <input
                      style={{ ...inputStyle, maxWidth: 560, fontFamily: "monospace", fontSize: 11 }}
                      value={sasSasUrl}
                      onChange={(e) => setSasSasUrl(e.target.value)}
                      placeholder="https://youraccount.blob.core.windows.net/yourcontainer?sv=…"
                    />
                    <div style={helpText}>
                      Generate a container-level SAS URL in Azure Portal → Storage account → Containers → your container → Generate SAS.
                      Grant <strong>Read</strong>, <strong>Write</strong>, <strong>List</strong> permissions. We'll validate it before saving.
                    </div>
                  </div>
                  <button
                    onClick={handleConnectBlobSas}
                    disabled={connecting || !sasSasUrl.trim() || !folderName.trim()}
                    style={{ ...btnPrimary, display: "block", width: "100%", padding: "11px 18px", fontSize: 14, opacity: (connecting || !sasSasUrl.trim() || !folderName.trim()) ? 0.7 : 1, marginBottom: 12 }}
                  >
                    {connecting ? "Validating…" : "Connect Azure Blob (SAS)"}
                  </button>
                  <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
                    The SAS URL is stored securely and never shared. We'll warn you before it expires — set the longest practical expiry when generating it.
                  </p>
                </>
              )}

              {/* Azure Blob Service Principal */}
              {selectedProvider === "azure_blob_sp" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 18 }}>
                    {[
                      { title: "No expiry to manage", desc: "Unlike SAS tokens, service principal credentials don't expire on a schedule — connect once and it keeps working." },
                      { title: "Your own storage", desc: "Data stays in your own Azure subscription with full control over retention, access policies, and lifecycle rules." },
                      { title: "Least-privilege access", desc: "Grant Storage Blob Data Contributor to a dedicated app registration — no broader permissions than necessary." },
                      { title: "Enterprise-grade", desc: "Suitable for regulated environments where credential rotation, audit trails, and IAM governance are required." },
                    ].map((f) => (
                      <div key={f.title} style={{ ...card, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 5 }}>{f.title}</div>
                        <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <div style={fieldWrap}>
                      <label style={fieldLabel}>Tenant ID</label>
                      <input style={inputStyle} value={spTenantId} onChange={(e) => setSpTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-…" />
                    </div>
                    <div style={fieldWrap}>
                      <label style={fieldLabel}>Client ID (App ID)</label>
                      <input style={inputStyle} value={spClientId} onChange={(e) => setSpClientId(e.target.value)} placeholder="xxxxxxxx-xxxx-…" />
                    </div>
                    <div style={fieldWrap}>
                      <label style={fieldLabel}>Client secret</label>
                      <input style={inputStyle} type="password" value={spSecret} onChange={(e) => setSpSecret(e.target.value)} placeholder="Your service principal secret" />
                    </div>
                    <div style={fieldWrap}>
                      <label style={fieldLabel}>Storage account name</label>
                      <input style={inputStyle} value={spAccount} onChange={(e) => setSpAccount(e.target.value)} placeholder="yourstorageaccount" />
                    </div>
                    <div style={fieldWrap}>
                      <label style={fieldLabel}>Container name</label>
                      <input style={inputStyle} value={spContainer} onChange={(e) => setSpContainer(e.target.value)} placeholder="compliance-evidence" />
                    </div>
                  </div>
                  <div style={{ ...helpText, marginBottom: 14 }}>
                    Create a service principal in your Azure tenant (App registrations → New registration), then grant it <strong>Storage Blob Data Contributor</strong> on the container via IAM → Add role assignment. Copy the tenant ID, client (app) ID, and a new client secret here.
                  </div>
                  <button
                    onClick={handleConnectBlobSp}
                    disabled={connecting || !spTenantId.trim() || !spClientId.trim() || !spSecret.trim() || !spAccount.trim() || !spContainer.trim()}
                    style={{ ...btnPrimary, display: "block", width: "100%", padding: "11px 18px", fontSize: 14, opacity: (connecting || !spTenantId.trim() || !spClientId.trim() || !spSecret.trim() || !spAccount.trim() || !spContainer.trim()) ? 0.7 : 1, marginBottom: 12 }}
                  >
                    {connecting ? "Validating credentials…" : "Connect Azure Blob (Service Principal)"}
                  </button>
                  <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
                    The client secret is encrypted at rest using AES-256-GCM with a key unique to your organisation. It is never transmitted in plaintext after this point.
                  </p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
